import { c as _c } from "react/compiler-runtime";
import { ProgressBar } from '@systemeio/components';
import { CheckCircleIcon, InfoCircleIcon, XCircleIcon } from '@systemeio/icons';
import { useCourse } from 'modules/courses/courses/hooks/use-course';
import { StudentProgressExportFailReasonEnum } from 'modules/courses/students/enums/student-progress-export-fail-reason-enum';
import { StudentProgressExportStatusEnum } from 'modules/courses/students/enums/student-progress-export-status-enum';
import { useCancelStudentProgressExport } from 'modules/courses/students/hooks/use-cancel-student-progress-export';
import { useStudentProgressExportStatusPolling } from 'modules/courses/students/hooks/use-student-progress-export-status-polling';
import type { NotificationStudentProgressExportItemInterface } from 'modules/courses/students/types/student-progress-export-notification-interface';
import { NotificationItemView } from 'modules/notifications/components/notification-item-view';
import { type ActionMenuItemProps } from 'shared/components';
import { RelativeTime } from 'shared/components/relative-time';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
interface StudentProgressExportNotificationProps {
  actions?: ActionMenuItemProps[];
  className?: string;
  notification: NotificationStudentProgressExportItemInterface;
}
const StudentProgressExportNotificationItem = t0 => {
  const $ = _c(7);
  const {
    actions,
    className,
    notification: notificationItem
  } = t0;
  const {
    user
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const {
    cancelStudentProgressExport,
    isCancelling
  } = useCancelStudentProgressExport();
  const t1 = notificationItem.data.status === StudentProgressExportStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.exportId || $[1] !== t1) {
    t2 = {
      exportId: notificationItem.data.exportId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.exportId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    exportStatus,
    mutate
  } = useStudentProgressExportStatusPolling(t2);
  const notification = {
    ...notificationItem,
    data: {
      ...notificationItem.data,
      ...(exportStatus ?? {})
    }
  };
  const {
    courseData
  } = useCourse(notification.data.courseId ?? undefined);
  const onCancelExport = async () => {
    await cancelStudentProgressExport(notification.data.exportId);
    await mutate(_temp, false);
  };
  const getIcon = () => {
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return <InfoCircleIcon className="text-icon-warning size-5 shrink-0" />;
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return <CheckCircleIcon className="text-icon-success size-5 shrink-0" />;
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          return <XCircleIcon className="text-icon-error size-5 shrink-0" />;
        }
      default:
        {
          return <InfoCircleIcon className="text-icon-warning size-5 shrink-0" />;
        }
    }
  };
  const getMessage = () => {
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return t("dashboard.notifications.student_export.running");
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return t("dashboard.notifications.student_export.success.title");
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.CancelledByUser:
              {
                return t("dashboard.notifications.student_export.failed.cancelled_by_user");
              }
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return t("dashboard.notifications.student_export.failed.time_exceeded");
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return t("dashboard.notifications.student_export.failed.rows_not_found");
              }
          }
          return notification.message;
        }
      default:
        {
          return notification.message;
        }
    }
  };
  const getDescription = () => {
    const courseName = courseData?.name ? courseData.name : t("dashboard.notifications.student_export.all_courses");
    const info = t("dashboard.notifications.student_export.export_info", {
      courseName
    });
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return <ProgressBar labelPosition="side" value={notification.data.progressPercent} />;
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return t("dashboard.notifications.student_export.failed.time_exceeded_description");
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return t("dashboard.notifications.student_export.failed.rows_not_found_description");
              }
            default:
              {
                return null;
              }
          }
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return <div className="flex flex-col gap-1"><span>{info}</span><span className="text-text-primary">{t("dashboard.notifications.student_export.success.link")}</span>{user && <span><RelativeTime time={notification.data.updatedAt} /></span>}</div>;
        }
      default:
        {
          return;
        }
    }
  };
  const t3 = notification.read;
  let t4;
  if ($[3] !== getIcon) {
    t4 = getIcon();
    $[3] = getIcon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = getMessage();
  let t6;
  if ($[5] !== getDescription) {
    t6 = getDescription();
    $[5] = getDescription;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return <NotificationItemView className={className} isRead={t3} actions={actions} icon={t4} message={t5} description={t6} showCancelButton={notification.data.status === StudentProgressExportStatusEnum.Running} isCancelling={isCancelling} onCancel={onCancelExport} />;
};
export default StudentProgressExportNotificationItem;
function _temp(data) {
  return data && {
    ...data,
    status: StudentProgressExportStatusEnum.Failed,
    failReason: StudentProgressExportFailReasonEnum.CancelledByUser
  };
}